import { useState } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';

export default function Settings() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    checkins: true,
    payments: true
  });

  return (
    <DashboardLayout>
      <Header title="Settings" />
      <div className="p-8 max-w-4xl" data-testid="settings-page">
        <div className="bg-zinc-900/50 border border-white/10 rounded-sm mb-6">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <User className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-white">Profile Settings</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Name</Label>
                <Input defaultValue={user?.name || 'Admin'} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="settings-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                <Input defaultValue={user?.email || 'admin@fitpulse.com'} className="bg-zinc-900 border-white/10 text-white rounded-sm" disabled data-testid="settings-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Role</Label>
              <Input defaultValue={user?.role || 'Administrator'} className="bg-zinc-900 border-white/10 text-white rounded-sm" disabled data-testid="settings-role-input" />
            </div>
            <Button className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm" data-testid="save-profile-btn">
              Save Changes
            </Button>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-white/10 rounded-sm mb-6">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <Bell className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-white">Notification Preferences</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Email Notifications</p>
                <p className="text-xs text-zinc-500 mt-1">Receive notifications via email</p>
              </div>
              <Switch checked={notifications.email} onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })} data-testid="email-notifications-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Push Notifications</p>
                <p className="text-xs text-zinc-500 mt-1">Receive push notifications in browser</p>
              </div>
              <Switch checked={notifications.push} onCheckedChange={(checked) => setNotifications({ ...notifications, push: checked })} data-testid="push-notifications-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Check-in Alerts</p>
                <p className="text-xs text-zinc-500 mt-1">Get notified when members check in</p>
              </div>
              <Switch checked={notifications.checkins} onCheckedChange={(checked) => setNotifications({ ...notifications, checkins: checked })} data-testid="checkin-notifications-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">Payment Alerts</p>
                <p className="text-xs text-zinc-500 mt-1">Get notified for new payments</p>
              </div>
              <Switch checked={notifications.payments} onCheckedChange={(checked) => setNotifications({ ...notifications, payments: checked })} data-testid="payment-notifications-switch" />
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-white/10 rounded-sm mb-6">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-white">Security</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Current Password</Label>
              <Input type="password" placeholder="Enter current password" className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="current-password-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">New Password</Label>
                <Input type="password" placeholder="Enter new password" className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Confirm Password</Label>
                <Input type="password" placeholder="Confirm new password" className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="confirm-password-input" />
              </div>
            </div>
            <Button className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm" data-testid="change-password-btn">
              Change Password
            </Button>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-white/10 rounded-sm">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <SettingsIcon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-white">About</h3>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-zinc-400">App Version</span>
              <span className="text-sm text-white font-medium">1.0.0</span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-zinc-400">Theme</span>
              <span className="text-sm text-white font-medium">Dark (Neon Lime)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Last Updated</span>
              <span className="text-sm text-white font-medium">April 2026</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

